import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Megaphone, Settings, Inbox, Plug, Trash2, Send, CheckCircle2, Circle, Copy } from "lucide-react";
import OwlLogo from "@/components/OwlLogo";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const TABS = [
  { id: "settings", label: "Réglages de l'app", icon: Settings },
  { id: "announcements", label: "Annonces", icon: Megaphone },
  { id: "messages", label: "Messages de support", icon: Inbox },
  { id: "connect", label: "Connecter l'app mobile", icon: Plug },
];

const DEFAULT_CONFIG = {
  app_name: "Chouette Apprentissage",
  tagline: "",
  companion_name: "Chouette",
  welcome_message: "",
  app_store_url: "",
  play_store_url: "",
  support_email: "",
  site_url: "",
};

const LoginScreen = ({ onLoggedIn }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/login", { email, password });
      const { data } = await api.get("/auth/me");
      toast.success("Bienvenue !");
      onLoggedIn(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-grid-paper flex items-center justify-center px-6 py-24">
      <Reveal className="w-full max-w-md">
        <form onSubmit={submit} className="card-paper p-8 space-y-5" data-testid="admin-login-form">
          <div className="flex flex-col items-center text-center">
            <OwlLogo className="w-14 h-14" />
            <h1 className="mt-4 font-serif text-2xl font-bold" data-testid="admin-login-title">Console d'administration</h1>
            <p className="mt-1 text-sm text-ink/60">Chouette Apprentissage</p>
          </div>
          <div>
            <label htmlFor="admin-email" className="text-sm font-bold">E-mail</label>
            <input id="admin-email" type="email" className="field mt-1.5" placeholder="admin@chouetteapprentissage.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-sm font-bold">Mot de passe</label>
            <input id="admin-password" type="password" className="field mt-1.5" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" />
          </div>
          <button type="submit" disabled={loading} className="btn-coral w-full" data-testid="admin-login-submit">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
      </Reveal>
    </main>
  );
};

const SettingsTab = ({ config, reload }) => {
  const [form, setForm] = useState(config);
  useEffect(() => setForm(config), [config]);
  const [saving, setSaving] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/config", form);
      toast.success("Réglages enregistrés — l'app mobile les recevra à sa prochaine synchronisation.");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    { name: "app_name", label: "Nom de l'application", testid: "settings-app-name" },
    { name: "tagline", label: "Slogan affiché sur le site", testid: "settings-tagline" },
    { name: "companion_name", label: "Nom du compagnon virtuel", testid: "settings-companion-name" },
    { name: "welcome_message", label: "Message d'accueil du compagnon", testid: "settings-welcome-message" },
    { name: "app_store_url", label: "Lien App Store", testid: "settings-app-store-url" },
    { name: "play_store_url", label: "Lien Google Play", testid: "settings-play-store-url" },
    { name: "support_email", label: "E-mail de support", testid: "settings-support-email" },
    { name: "site_url", label: "URL du site", testid: "settings-site-url" },
  ];

  return (
    <div className="card-paper p-6 sm:p-8" data-testid="admin-settings-tab">
      <h2 className="font-serif text-2xl font-bold">Réglages de l'application</h2>
      <p className="mt-1 text-sm text-ink/60">
        Ces informations sont servies à l'app mobile via l'API publique du slug <span className="font-mono text-coral font-semibold">chouette-apprentissage</span> et affichées sur le site.
      </p>
      <div className="mt-6 grid sm:grid-cols-2 gap-5">
        {fields.map((f) => (
          <div key={f.name} className={f.name === "welcome_message" ? "sm:col-span-2" : ""}>
            <label htmlFor={f.name} className="text-sm font-bold">{f.label}</label>
            <input id={f.name} name={f.name} className="field mt-1.5" value={form[f.name] || ""} onChange={change} data-testid={f.testid} />
          </div>
        ))}
      </div>
      <button onClick={save} disabled={saving} className="btn-coral mt-7" data-testid="settings-save-button">
        {saving ? "Enregistrement…" : "Enregistrer les réglages"}
      </button>
    </div>
  );
};

const AnnouncementsTab = ({ announcements, reload }) => {
  const [form, setForm] = useState({ title: "", body: "", level: "info" });
  const [sending, setSending] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Titre et message sont obligatoires.");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/announcements", form);
      toast.success("Annonce publiée — elle apparaîtra dans l'app.");
      setForm({ title: "", body: "", level: "info" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success("Annonce supprimée.");
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6 items-start" data-testid="admin-announcements-tab">
      <form onSubmit={create} className="card-paper p-6 sm:p-8 space-y-4">
        <h2 className="font-serif text-2xl font-bold">Nouvelle annonce</h2>
        <div>
          <label htmlFor="ann-title" className="text-sm font-bold">Titre</label>
          <input id="ann-title" className="field mt-1.5" placeholder="Nouveau chapitre disponible" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
        </div>
        <div>
          <label htmlFor="ann-body" className="text-sm font-bold">Message</label>
          <textarea id="ann-body" rows={4} className="field mt-1.5 resize-none" placeholder="Le texte de votre annonce…" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="announcement-body-input" />
        </div>
        <div>
          <label htmlFor="ann-level" className="text-sm font-bold">Type</label>
          <select id="ann-level" className="field mt-1.5" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="announcement-level-select">
            <option value="info">Information</option>
            <option value="success">Nouveauté</option>
            <option value="warning">Alerte / maintenance</option>
          </select>
        </div>
        <button type="submit" disabled={sending} className="btn-coral w-full" data-testid="announcement-submit-button">
          <Send className="w-4 h-4" />
          {sending ? "Publication…" : "Publier l'annonce"}
        </button>
      </form>

      <div className="space-y-3.5">
        {announcements.length === 0 && (
          <div className="card-paper p-6 text-sm text-ink/50" data-testid="announcements-empty">Aucune annonce pour le moment.</div>
        )}
        {announcements.map((ann) => (
          <div key={ann.id} className="card-paper p-5 flex items-start justify-between gap-4" data-testid={`announcement-card-${ann.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span
                  className={`text-[10px] font-mono font-bold uppercase tracking-wider rounded-full px-2 py-0.5 ${
                    ann.level === "warning" ? "bg-hi text-ink" : ann.level === "success" ? "bg-emerald-100 text-emerald-700" : "bg-ink/5 text-ink/60"
                  }`}
                >
                  {ann.level}
                </span>
                <p className="font-bold text-sm">{ann.title}</p>
              </div>
              <p className="mt-1.5 text-sm text-ink/65 leading-relaxed">{ann.body}</p>
              <p className="mt-2 text-xs text-ink/40">{new Date(ann.created_at).toLocaleString("fr-FR")}</p>
            </div>
            <button onClick={() => remove(ann.id)} className="p-2 rounded-lg text-ink/40 hover:text-coral hover:bg-coral/10 transition-colors" aria-label="Supprimer" data-testid={`announcement-delete-${ann.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

const MessagesTab = ({ messages, reload }) => {
  const toggle = async (msg) => {
    try {
      await api.patch(`/admin/messages/${msg.id}`, { handled: !msg.handled });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-3.5" data-testid="admin-messages-tab">
      {messages.length === 0 && (
        <div className="card-paper p-6 text-sm text-ink/50" data-testid="messages-empty">Aucun message reçu pour le moment.</div>
      )}
      {messages.map((msg) => (
        <div key={msg.id} className={`card-paper p-5 ${msg.handled ? "opacity-60" : ""}`} data-testid={`message-card-${msg.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-bold text-sm" data-testid={`message-subject-${msg.id}`}>{msg.subject}</p>
              <p className="text-xs text-ink/50 mt-0.5">
                {msg.name} · <a href={`mailto:${msg.email}`} className="underline">{msg.email}</a> · {new Date(msg.created_at).toLocaleString("fr-FR")}
              </p>
              <p className="mt-3 text-sm text-ink/75 leading-relaxed whitespace-pre-line">{msg.message}</p>
            </div>
            <button
              onClick={() => toggle(msg)}
              className={`p-2 rounded-lg transition-colors shrink-0 ${msg.handled ? "text-emerald-600" : "text-ink/35 hover:text-emerald-600"}`}
              aria-label={msg.handled ? "Marquer non traité" : "Marquer traité"}
              data-testid={`message-toggle-${msg.id}`}
            >
              {msg.handled ? <CheckCircle2 className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const ConnectTab = () => {
  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const endpoints = [
    { method: "GET", path: `/api/apps/chouette-apprentissage/config`, desc: "Réglages de l'app (nom du compagnon, message d'accueil, liens stores…)" },
    { method: "GET", path: `/api/apps/chouette-apprentissage/announcements`, desc: "Annonces actives à afficher dans l'app" },
    { method: "POST", path: `/api/support`, desc: "Envoyer un message de support depuis l'app (accusé de réception e-mail automatique)" },
  ];
  const snippets = [
    {
      label: "1. Module de connexion — copiez ce fichier dans socratic-learn-13 (api.js)",
      code: `// api.js — connexion de l'app au site
const API = "${API_URL}";
const SLUG = "chouette-apprentissage";

// Réglages de l'app (nom du compagnon, message d'accueil, liens stores…)
export async function getAppConfig() {
  const res = await fetch(API + "/apps/" + SLUG + "/config");
  if (!res.ok) throw new Error("Config indisponible");
  return res.json();
}

// Annonces actives publiées depuis la console
export async function getAnnouncements() {
  const res = await fetch(API + "/apps/" + SLUG + "/announcements");
  return res.json();
}

// Message de support envoyé depuis l'app (accusé de réception auto)
export async function sendSupport(name, email, subject, message) {
  const res = await fetch(API + "/support", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, email, subject, message }),
  });
  return res.json();
}`,
    },
    {
      label: "2. Afficher le compagnon avec les réglages du site",
      code: `import { useEffect, useState } from "react";\nimport { getAppConfig } from "./api";\n\nexport function useAppConfig() {\n  const [config, setConfig] = useState(null);\n  useEffect(() => {\n    getAppConfig().then(setConfig).catch(() => {});\n  }, []);\n  return config;\n}\n// Puis, dans vos écrans : config.companion_name,\n// config.welcome_message, config.app_store_url…`,
    },
    {
      label: "3. Bandeau d'annonces — composant prêt à coller (bulles jaunes de l'app)",
      code: `import { useEffect, useState } from "react";
import { getAnnouncements } from "./api";

// Bulles jaunes, comme les messages de Chouette dans l'app
export function AnnouncementBanner() {
  const [annonces, setAnnonces] = useState([]);
  useEffect(() => {
    getAnnouncements().then(setAnnonces).catch(() => {});
  }, []);
  if (!annonces.length) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {annonces.map((a) => (
        <div key={a.id} style={{
          background: "#F6BE3C", color: "#211A13",
          border: "2px solid #211A13", borderRadius: 20,
          padding: "14px 18px",
        }}>
          <strong>{a.title}</strong>
          <p style={{ margin: "4px 0 0", fontSize: 14 }}>{a.body}</p>
        </div>
      ))}
    </div>
  );
}

// Utilisation sur l'écran d'accueil : <AnnouncementBanner />`,
    },
  ];
  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Code copié !");
  };

  return (
    <div className="card-paper p-6 sm:p-8" data-testid="admin-connect-tab">
      <h2 className="font-serif text-2xl font-bold">Connecter l'application mobile</h2>
      <p className="mt-2 text-sm text-ink/65 leading-relaxed">
        En clair : (1) on dépose deux petits fichiers dans votre application, (2) l'app ira chercher toute seule les réglages et les annonces sur votre site, (3) vous pilotez tout depuis cette console. Vous ne savez pas coder ? Copiez le message ci-dessous et collez-le dans le chat de votre application socratic-learn-13 : son assistant fera l'installation pour vous.
      </p>

      <div className="mt-4 rounded-xl border-2 border-dashed border-coral/40 bg-coral/5 p-5" data-testid="connect-agent-message">
        <div className="flex items-center justify-between gap-3">
          <p className="font-bold text-sm">Message à coller dans le chat de socratic-learn-13</p>
          <button onClick={() => copy(`Mon site vitrine (Chouette Apprentissage) expose une API publique pour mon app. Intègre dans mon code ces trois fichiers : ${API_URL}/../integration-kit/api.js (services de connexion, URL de l'API déjà configurée), ${API_URL}/../integration-kit/AnnouncementBanner.jsx (bandeau d'annonces en bulles jaunes) et ${API_URL}/../integration-kit/TrialBadge.jsx (pastille « 14 jours d'essai gratuit »). Place <AnnouncementBanner /> sur mon écran d'accueil sous la carte « Choisis une matière », et <TrialBadge /> près du titre d'accueil. Le slug de l'app est chouette-apprentissage.`)} className="inline-flex items-center gap-1.5 rounded-lg bg-white border border-ink/10 px-2.5 py-1.5 text-[11px] font-bold hover:border-coral transition-colors" data-testid="connect-agent-copy">
            <Copy className="w-3.5 h-3.5 text-coral" />
            Copier
          </button>
        </div>
        <pre className="mt-3 text-[11px] font-mono leading-relaxed text-ink/75 whitespace-pre-wrap break-words">{`Mon site vitrine (Chouette Apprentissage) expose une API publique pour mon app. Intègre dans mon code ces trois fichiers : ${API_URL}/../integration-kit/api.js (services de connexion, URL de l'API déjà configurée), ${API_URL}/../integration-kit/AnnouncementBanner.jsx (bandeau d'annonces en bulles jaunes) et ${API_URL}/../integration-kit/TrialBadge.jsx (pastille « 14 jours d'essai gratuit »). Place <AnnouncementBanner /> sur mon écran d'accueil sous la carte « Choisis une matière », et <TrialBadge /> près du titre d'accueil. Le slug de l'app est chouette-apprentissage.`}</pre>
      </div>
      <button
        onClick={() => copy(API_URL)}
        className="mt-4 w-full flex items-center justify-between gap-3 rounded-xl border border-ink/15 bg-paper2/60 px-4 py-3 text-left font-mono text-xs sm:text-sm hover:border-coral transition-colors"
        data-testid="connect-api-base-copy"
      >
        <span className="break-all">{API_URL}</span>
        <Copy className="w-4 h-4 shrink-0 text-coral" />
      </button>

      <div className="mt-6 space-y-4">
        {snippets.map((sn, i) => (
          <div key={i} className="rounded-xl border border-ink/10 overflow-hidden" data-testid={`connect-snippet-${i}`}>
            <p className="px-4 py-2.5 text-xs font-bold bg-paper2/70">{sn.label}</p>
            <div className="relative">
              <pre className="px-4 py-3.5 text-[11px] sm:text-xs font-mono leading-relaxed overflow-x-auto text-ink/80">{sn.code}</pre>
              <button
                onClick={() => copy(sn.code)}
                className="absolute top-2 right-2 inline-flex items-center gap-1.5 rounded-lg bg-white border border-ink/10 px-2.5 py-1.5 text-[11px] font-bold hover:border-coral transition-colors"
                data-testid={`connect-copy-${i}`}
              >
                <Copy className="w-3.5 h-3.5 text-coral" />
                Copier
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 space-y-3">
        {endpoints.map((ep, i) => (
          <div key={i} className="rounded-xl border border-ink/10 p-4" data-testid={`connect-endpoint-${i}`}>
            <p className="flex items-center gap-2 font-mono text-xs sm:text-sm">
              <span className={`font-bold rounded-md px-2 py-0.5 ${ep.method === "GET" ? "bg-hi text-ink" : "bg-coral text-ink"}`}>{ep.method}</span>
              <span className="break-all">{ep.path}</span>
            </p>
            <p className="mt-1.5 text-xs text-ink/55">{ep.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-xl border border-ink/10 bg-paper2/50 p-5" data-testid="connect-admin-email-guide">
        <p className="font-bold text-sm">Recevoir les notifications admin</p>
        <p className="mt-1.5 text-xs text-ink/60 leading-relaxed">
          Les notifications de nouveaux messages partent vers admin@chouetteapprentissage.com. Pour qu'elles arrivent : créez cette boîte chez votre hébergeur e-mail (OVH, Google Workspace…) — les enregistrements MX du domaine doivent être actifs. Un simple transfert vers votre adresse personnelle suffit aussi. Pour recevoir sur une autre adresse sans attendre, ajoutez ADMIN_NOTIF_EMAIL="votre-adresse@exemple.fr" dans le fichier .env du backend, puis redémarrez le backend.
        </p>
      </div>

      <p className="mt-5 text-xs text-ink/45 leading-relaxed">
        Astuce : modifiez le nom du compagnon ou publiez une annonce depuis cette console, puis relancez l'app — elle affichera immédiatement les nouveaux réglages. Quand le domaine chouetteapprentissage.com sera branché, remplacez simplement l'URL de l'API par https://chouetteapprentissage.com/api.
      </p>
    </div>
  );
};

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("settings");
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [announcements, setAnnouncements] = useState([]);
  const [messages, setMessages] = useState([]);

  const loadAll = async () => {
    try {
      const [c, a, m] = await Promise.all([api.get("/admin/config"), api.get("/admin/announcements"), api.get("/admin/messages")]);
      setConfig(c.data);
      setAnnouncements(a.data);
      setMessages(m.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  useEffect(() => {
    api
      .get("/auth/me")
      .then(({ data }) => {
        setUser(data);
        loadAll();
      })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, []);

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
  };

  if (checking) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-paper" data-testid="admin-checking">
        <p className="text-ink/50 text-sm">Vérification de la session…</p>
      </main>
    );
  }

  if (!user) return <LoginScreen onLoggedIn={(u) => { setUser(u); loadAll(); }} />;

  return (
    <main className="min-h-screen bg-paper2/40 pb-20" data-testid="admin-dashboard">
      <header className="bg-chalk text-paper">
        <div className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <OwlLogo className="w-9 h-9" />
            <div>
              <p className="font-serif font-bold leading-tight">Console d'administration</p>
              <p className="text-xs text-paper/60">Chouette Apprentissage · {user.email}</p>
            </div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-paper/25 px-4 py-2 text-sm font-semibold hover:bg-paper/10 transition-colors" data-testid="admin-logout-button">
            <LogOut className="w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 mt-8">
        <div className="flex flex-wrap gap-2" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-bold transition-all ${
                tab === t.id ? "bg-ink text-paper" : "bg-white border border-ink/10 text-ink/60 hover:text-ink"
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-7">
          {tab === "settings" && <SettingsTab config={config} reload={loadAll} />}
          {tab === "announcements" && <AnnouncementsTab announcements={announcements} reload={loadAll} />}
          {tab === "messages" && <MessagesTab messages={messages} reload={loadAll} />}
          {tab === "connect" && <ConnectTab />}
        </div>
      </div>
    </main>
  );
}